import React, { useCallback, useEffect, useMemo, useState } from "react";
import Table from "@components/table";
import Svg from "@components/svg";
import { deletesvg, edit, eye, switchOff, switchOn } from "@assets/icons";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import { Coupon } from "@apis/index";
import { toast } from "react-toastify";
import { useModal } from "@context/ModalContext";
import DeleteModal from "@components/delete-modal";
import { useNavigate } from "react-router-dom";

const CouponList = ({ state }: { state?: any }) => {
  let { data: user } = useSelector((state: RootState) => state.user);
  if (state) {
    user = state;
  }

  const {
    items: coupons,
    totalItems,
    totalPages,
  } = useSelector((state: RootState) => state[user.role + "Coupons"]);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const { showModal } = useModal();
  const navigate = useNavigate();

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      await Coupon.list({
        state: user.role + "Coupons",
        params: {
          page,
          limit: 30,
          role: user.role,
          search,
          ...(user.role === "store" && { storeId: user.id }),
        },
      });
    } catch (err: any) {
      toast.error(err?.message || "Failed to fetch coupons");
    } finally {
      setLoading(false);
    }
  }, [page, user.role, user.id]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const getTimeLeft = useCallback((startDate: string, endDate: string) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) {
      const diff = Math.ceil(
        (start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
      );
      return `Starts in ${diff} day${diff > 1 ? "s" : ""}`;
    }

    if (now > end) {
      return "Expired";
    }

    const diff = Math.ceil(
      (end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );
    return `${diff} day${diff > 1 ? "s" : ""} left`;
  }, []);

  const columns: any = useMemo(
    () => [
      {
        title: "SN",
        key: "sn",
        render: (_, i) => i,
      },
      { title: "Title", key: "title", render: (item: any) => item.title },
      { title: "Code", key: "code", render: (item: any) => item.code },
      {
        title: "Module",
        key: "module",
        render: (item: any) => item.module?.name || "-",
      },

      {
        title: "Coupon Type",
        key: "type",
        render: (item: any) => item.couponType,
      },
      {
        title: "Store",
        key: "store",
        render: (item: any) => item.store?.name || "-",
      },
      {
        title: "Zone",
        key: "zone",
        render: (item: any) => item.zone?.name || "-",
      },
      {
        title: "Category",
        key: "category",
        render: (item: any) => item.category?.name || "-",
      },
      {
        title: "Start Date",
        key: "startDate",
        render: (item: any) => new Date(item.startDate).toLocaleDateString(),
      },
      {
        title: "End Date",
        key: "endDate",
        render: (item: any) => new Date(item.endDate).toLocaleDateString(),
      },
      {
        title: "Time Left",
        key: "timeLeft",
        render: (item: any) => getTimeLeft(item.startDate, item.endDate),
      },

      {
        title: "Discount",
        key: "discount",
        render: (item: any) =>
          `${item.discountInfos?.amount || 0}${
            item.discountInfos?.type === "percentage" ? "%" : ""
          }`,
      },
      {
        title: "Status",
        key: "status",
        render: (item: any) => (
          <Svg
            src={item.isActive ? switchOn : switchOff}
            onClick={async () => {
              await Coupon.updateStatus({
                state: user.role + "Coupons",
                data: { isActive: !item.isActive },
                id: item.id,
              })
                .then(() => {
                  toast.success(
                    `Coupon set to ${item.isActive ? "inactive" : "active"}`,
                  );
                  fetchCoupons();
                })
                .catch(() => {});
            }}
          />
        ),
      },
      {
        title: "Show Promocode",
        key: "isShow",
        render: (item: any) => (
          <Svg
            src={item.isShow ? switchOn : switchOff}
            onClick={async () => {
              await Coupon.update({
                state: user.role + "Coupons",
                data: { isShow: !item.isShow },
                id: item.id,
              })
                .then(() => {
                  toast.success(
                    `Promocode visibility set to ${item.isShow ? "hidden" : "visible"}`,
                  );
                  fetchCoupons();
                })
                .catch(() => {});
            }}
          />
        ),
      },
    ],
    [getTimeLeft, user.role, fetchCoupons],
  );

  return (
    <Table
      text="Coupon List"
      columns={columns}
      data={coupons}
      loading={loading}
      totalItems={totalItems}
      totalPages={totalPages}
      itemsPerPage={30}
      pageControlled={page}
      onPageChange={setPage}
      hasSearchbar={true}
      onSearchChange={setSearch}
      actions={useMemo(
        () => [
          {
            key: "view",
            icon: eye,
            onClick: (item: any) =>
              navigate(`/${user.role}/coupon-setup/${item.id}`, {
                state: item,
              }),
          },
          {
            key: "edit",
            icon: edit,
            onClick: (item: any) =>
              navigate(`/${user.role}/coupon-setup/${item.id}`, {
                state: item,
              }),
          },
          {
            key: "delete",
            icon: deletesvg,
            onClick: async (item: any) => {
              showModal(
                <DeleteModal
                  state={user.role + "Coupons"}
                  api={Coupon}
                  id={item.id}
                  onSuccess={() => fetchCoupons()}
                />,
              );
            },
          },
        ],
        [user.role, navigate, showModal, fetchCoupons],
      )}
    />
  );
};

export default CouponList;
